/**
 * Estado de la aplicación CRT.
 *
 * El terminal funciona como un programa DOS clásico: hay un "log" que
 * acumula líneas escritas previamente y una "línea de prompt" activa
 * donde el usuario está escribiendo. Cuando el usuario pulsa Enter, el
 * input se vuelca al log con el carácter que tecleó (o asteriscos si
 * es password) y se transita a la siguiente vista.
 *
 * No usamos framework reactivo — `render.ts` pinta el estado completo
 * en cada cambio. Para 50 líneas máximas en pantalla esto es
 * totalmente trivial en coste.
 */

import type { AuthenticatedPilot } from '@51esc/shared';

import type { LegalDocumentId } from '../legal/content.js';
import type { TimeOfDayPreference } from './crt-controls.js';
import { DEFAULT_PROFILE_ID, type MonitorProfileId } from './monitor-profiles.js';

/** Identificador de cada paso del flujo. Suficiente como string. */
export type ViewId =
  | 'menu'
  | 'login_callsign'
  | 'login_password'
  | 'login_busy'
  | 'register_callsign'
  | 'register_email'
  | 'register_password'
  | 'register_password_confirm'
  | 'register_turnstile'
  | 'register_busy'
  | 'register_done'
  | 'verify_email_checking'
  | 'verify_email_done'
  | 'verify_email_resend'
  | 'verify_email_resent'
  | 'recovery_request'
  | 'recovery_sent'
  | 'recovery_set_password'
  | 'config'
  | 'config_screen_profile'
  | 'config_time_of_day'
  | 'about'
  | 'about_doc';

export type LogLevel = 'normal' | 'msg' | 'warn' | 'info' | 'error' | 'title';

export interface LogLine {
  text: string;
  level: LogLevel;
}

export interface AppState {
  view: ViewId;
  log: LogLine[];
  /** Texto que el usuario está escribiendo en este momento. */
  inputBuffer: string;
  /**
   * Si true, el render oculta el input y muestra asteriscos por cada
   * carácter (campos de password).
   */
  inputIsPassword: boolean;
  /** Si true, el cursor parpadeante NO se pinta (estado "ocupado"). */
  inputDisabled: boolean;
  /** Datos en progreso del flujo de login. */
  loginData: { callsign: string | null };
  /** Datos en progreso del flujo de registro. */
  registerData: {
    callsign: string | null;
    email: string | null;
    password: string | null;
  };
  /** Token de Turnstile capturado mientras está la vista register_turnstile. */
  turnstileToken: string | null;
  /** Usuario logueado tras login exitoso (para mensaje de bienvenida). */
  authenticatedUser: AuthenticatedPilot | null;
  /**
   * Perfil de monitor CRT actualmente activo. Se inicializa desde
   * `loadStoredProfile()` en boot y se actualiza cuando el usuario
   * elige otro en CONFIG → PERFIL DE PANTALLA. El render lo usa para
   * pintar el `(X)` en la opción correcta del selector.
   */
  currentProfile: MonitorProfileId;
  /**
   * Preferencia de día/noche para la sala. Se inicializa desde
   * `loadStoredTimeOfDay()` y se actualiza en CONFIG → MODO DÍA/TARDE/NOCHE.
   * No afecta al propio CRT — solo lo persiste para que al volver
   * a la sala se aplique.
   */
  currentTimeOfDay: TimeOfDayPreference;
  /**
   * Documento legal seleccionado en el submenú ACERCA DE. La vista
   * `about_doc` lo lee para saber qué texto volcar en el terminal.
   * `null` mientras no se haya abierto ningún apartado.
   */
  aboutDocId: LegalDocumentId | null;
  /**
   * Token de verificación de email leído de `?token=` en
   * `/verify-email.html`. `null` si no venía token — se entra entonces
   * directamente al formulario de reenvío.
   */
  verifyToken: string | null;
  /**
   * Token de restablecimiento de contraseña leído de `?token=` en
   * `/recovery.html`. `null` en el modo "solicitar enlace".
   */
  resetToken: string | null;
}

export function createState(
  initialView: ViewId = 'menu',
  currentProfile: MonitorProfileId = DEFAULT_PROFILE_ID,
  currentTimeOfDay: TimeOfDayPreference = 'auto',
): AppState {
  return {
    view: initialView,
    log: [],
    inputBuffer: '',
    inputIsPassword: false,
    inputDisabled: false,
    loginData: { callsign: null },
    registerData: { callsign: null, email: null, password: null },
    turnstileToken: null,
    authenticatedUser: null,
    currentProfile,
    currentTimeOfDay,
    aboutDocId: null,
    verifyToken: null,
    resetToken: null,
  };
}

export function pushLine(state: AppState, text: string, level: LogLevel = 'normal'): void {
  state.log.push({ text, level });
}

/** Vacía el log. Se usa al volver al menú o entrar a una sub-vista. */
export function clearLog(state: AppState): void {
  state.log = [];
}

/** Resetea el buffer de input + flags asociados. */
export function clearInput(state: AppState): void {
  state.inputBuffer = '';
  state.inputIsPassword = false;
  state.inputDisabled = false;
}
